import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation, Link } from "react-router-dom";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import { BackButton } from "../../../../Components/Utils/Buttons";
import { NormalHeading } from "../../../../Components/Utils/Heading";
import axiosInstance from "../../../../Services/axiosCommon";
import NuDate from "../../../../Components/Utils/NuDate";
import NuAmount from "../../../../Components/Utils/NuAmount";
import { GetRole } from "../../../../Components/Utils/roles";
import AccessDenied from "../../../../Components/Utils/AccessDenied";

const AgentPriceListing = () => {
  const { comparisonId, agentId } = useParams();
  const location = useLocation();
  const isAdmin = GetRole();

  // Fetch agent price revision history from dedicated API
  const { data: historyResponse, isLoading: isHistoryLoading } = useQuery({
    queryKey: ["agentPriceHistory", agentId],
    queryFn: async () => {
      const res = await axiosInstance.get(
        `/shipping-comparison/agent-history/${agentId}`
      );
      return res?.data?.data || res?.data || [];
    },
    enabled: !!agentId && isAdmin,
  });

  // Fetch comparison details for context
  const { data: fetchedComparison, isLoading: isComparisonLoading } = useQuery({
    queryKey: ["singleShippingComparison", comparisonId],
    queryFn: async () => {
      const res = await axiosInstance.get(`/shipping-comparison/${comparisonId}`);
      return res?.data?.data || res?.data || null;
    },
    enabled: !!comparisonId && isAdmin,
  });

  if (!isAdmin) {
    return <AccessDenied />;
  }

  const comparison = fetchedComparison || location.state?.comparison;
  const agents = Array.isArray(comparison?.agents)
    ? comparison.agents
    : Array.isArray(comparison?.agents_detail)
      ? comparison.agents_detail
      : [];

  const currentAgent =
    agents.find((a) => String(a.agentId || a.id) === String(agentId)) ||
    location.state?.agent;

  const historyList = Array.isArray(historyResponse)
    ? historyResponse
    : Array.isArray(historyResponse?.history)
      ? historyResponse.history
      : Array.isArray(historyResponse?.data)
        ? historyResponse.data
        : Array.isArray(currentAgent?.history)
          ? currentAgent.history
          : [];

  const dataLoading = isHistoryLoading || (isComparisonLoading && !comparison);

  return (
    <div className="w-full h-full p-2 lg:px-4">
      {/* Header Bar */}
      <div className="w-full flex flex-row justify-between items-center gap-2 mb-3">
        <Breadcrumb
          items={[
            {
              name: "Shipping",
              link: "/shipping/shipping-comparison",
            },
            {
              name: "Shipping Comparison",
              link: "/shipping/shipping-comparison",
            },
            {
              name: `Comparison #${comparisonId}`,
              link: `/shipping/shipping-comparison/view/${comparisonId}`,
            },
            { name: "Agent Price Revisions" },
          ]}
        />
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link to={`/shipping/shipping-comparison/view/${comparisonId}`}>
            <BackButton link={true} />
          </Link>
        </div>
      </div>

      {/* Main Container */}
      <div className="w-full mx-auto min-h-[calc(100vh-145px)] h-auto bg-white rounded-md overflow-y-auto p-2 sm:p-4 mt-4">
        {dataLoading && !comparison ? (
          <div className="w-full h-[40vh] flex justify-center items-center">
            <div className="flex flex-row gap-2">
              <div className="w-3 h-3 rounded-full bg-baseColor-primary animate-bounce" />
              <div className="w-3 h-3 rounded-full bg-baseColor-primary animate-bounce [animation-delay:-.3s]" />
              <div className="w-3 h-3 rounded-full bg-baseColor-primary animate-bounce [animation-delay:-.5s]" />
              <div className="w-3 h-3 rounded-full bg-baseColor-primary animate-bounce [animation-delay:-.7s]" />
            </div>
          </div>
        ) : (
          <>
            <NormalHeading
              title={`Price Revision History - ${currentAgent?.agentName || currentAgent?.name || `Agent #${agentId}`
                }`}
            />

            {/* Summary Details Card */}
            <div className="flex flex-col md:flex-row gap-4 mt-2">
              <div className="w-full md:w-1/2 flex">
                <div className="w-full rounded-[20px] bg-white py-2 flex flex-col">
                  <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                    <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Comparison Scope</p>
                    <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs text-[#606279]">
                      {comparison?.description || `Comparison #${comparisonId}`}
                    </p>
                  </div>

                  {comparison?.prRef && (
                    <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                      <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">PR Reference</p>
                      <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs text-[#048178] font-medium">
                        {comparison.prRef}
                      </p>
                    </div>
                  )}

                  <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                    <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Shipping Agent</p>
                    <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs font-semibold text-gray-800">
                      {currentAgent?.agentName || currentAgent?.name || `Agent #${agentId}`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="w-full md:w-1/2 flex">
                <div className="w-full rounded-[20px] bg-white py-2 flex flex-col">
                  <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                    <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Current Price</p>
                    <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs font-bold text-gray-900">
                      <NuAmount
                        currency={comparison?.currency || currentAgent?.currency || "USD"}
                        value={currentAgent?.price || 0}
                      />
                    </p>
                  </div>

                  <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                    <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">Total Revisions</p>
                    <p className="w-1/2 py-2 pl-3 sm:pl-5 text-xs text-[#606279]">
                      {historyList.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* History Table */}
            <div className="w-full mt-6">
              <div className="w-full">
                <p className="pb-2 pt-2 px-2 text-sm font-medium text-[#111537] underline decoration-[#048178] decoration-2 underline-offset-4">
                  Price Revision History :
                </p>
              </div>

              <div className="w-full mt-2 overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200 rounded-md">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-[#111537]">
                      <th className="w-16 text-center py-2.5 px-3">Revision</th>
                      <th className="w-[22%] text-left py-2.5 px-4">Previous Price</th>
                      <th className="w-[22%] text-left py-2.5 px-4">New Price</th>
                      <th className="w-[26%] text-left py-2.5 px-4">Updated By</th>
                      <th className="w-[22%] text-left py-2.5 px-4">Updated Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-xs">
                    {historyList.length > 0 ? (
                      historyList.map((item, index) => {
                        const prevPrice = Number(item?.previousPrice || item?.oldPrice || item?.prevPrice || 0);
                        const newPrice = Number(item?.newPrice || item?.price || 0);
                        const updatedBy = item?.updatedByName || item?.userName || item?.user || "System / Admin";

                        return (
                          <tr
                            key={item?.id || index}
                            className="odd:bg-white even:bg-slate-50 text-[#606279] hover:bg-slate-100 transition-colors"
                          >
                            <td className="py-3 px-3 text-center font-medium">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
                                {index + 1}
                              </span>
                            </td>

                            <td className="py-3 px-4 text-left font-medium text-gray-500">
                              <NuAmount
                                currency={comparison?.currency || "USD"}
                                value={prevPrice}
                              />
                            </td>

                            <td className="py-3 px-4 text-left font-bold text-gray-900">
                              <NuAmount
                                currency={comparison?.currency || "USD"}
                                value={newPrice}
                              />
                            </td>

                            <td className="py-3 px-4 text-left font-medium text-gray-800">
                              {updatedBy}
                            </td>

                            <td className="py-3 px-4 text-left text-gray-500">
                              {item?.createdAt
                                ? NuDate({
                                  value: item.createdAt,
                                  format: "DD-MMM-YYYY, hh:mm A",
                                })
                                : NuDate({
                                  value: item?.date || new Date(),
                                  format: "DD-MMM-YYYY, hh:mm A",
                                })}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-6 text-center text-gray-400"
                        >
                          No price revision records found for this agent.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AgentPriceListing;
